import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Stack, Chip, IconButton, Tooltip, Typography, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Select, InputLabel, FormControl, OutlinedInput, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WorkIcon from '@mui/icons-material/Work';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getStaff, createStaffMember, updateStaffMember, deleteStaffMember } from '../../api/endpoints/staff';
import { getStaffRoles, createStaffRole, deleteStaffRole } from '../../api/endpoints/staffRoles';
import { uploadFile } from '../../api/endpoints/media';
import countryCodes from '../../utils/countryCodes';

const initialStaffForm = {
  name: '', email: '', password: '', uniqueId: '', phone: '', profileImage: '',
  addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', country: 'India',
  roles: [],
};

export default function StaffList() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Staff form modal
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [staffForm, setStaffForm] = useState(initialStaffForm);
  const [staffCountryCode, setStaffCountryCode] = useState('+91');
  const [staffEditId, setStaffEditId] = useState(null);
  const [staffSubmitting, setStaffSubmitting] = useState(false);

  // Role modal
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: '', uniqueId: '', description: '', accessiblePages: '' });
  const [roleSubmitting, setRoleSubmitting] = useState(false);

  // View detail
  const [viewStaff, setViewStaff] = useState(null);

  // Profile image
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteProcessing, setDeleteProcessing] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await getStaff(params);
      setStaff(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await getStaffRoles({ limit: 100 });
      setRoles(res.data.data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);
  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const openStaffModal = (member = null) => {
    setProfileImageFile(null);
    setProfileImagePreview(member?.profileImage || null);
    if (member) {
      setStaffEditId(member._id);
      let phone = member.phone || '';
      let detectedCode = '+91';
      if (phone.startsWith('+')) {
        const matched = countryCodes.find(c => phone.startsWith(c.value));
        if (matched) { detectedCode = matched.value; phone = phone.slice(matched.value.length); }
      }
      setStaffCountryCode(detectedCode);
      setStaffForm({
        name: member.name || '',
        email: member.email || '',
        password: '',
        uniqueId: member.uniqueId || '',
        phone: phone,
        profileImage: member.profileImage || '',
        addressLine1: member.addressLine1 || '',
        addressLine2: member.addressLine2 || '',
        city: member.city || '',
        state: member.state || '',
        pincode: member.pincode || '',
        country: member.country || 'India',
        roles: (member.roles || []).map(r => r._id || r),
      });
    } else {
      setStaffEditId(null);
      setStaffCountryCode('+91');
      setStaffForm(initialStaffForm);
    }
    setStaffModalOpen(true);
  };

  const handleStaffSubmit = async () => {
    if (!staffForm.name.trim() || !staffForm.email.trim() || (!staffEditId && !staffForm.password.trim())) {
      toast.error('Name, email, and password are required');
      return;
    }
    setStaffSubmitting(true);
    try {
      let profileImage = staffForm.profileImage || '';
      if (profileImageFile) {
        const uploadRes = await uploadFile(profileImageFile);
        profileImage = uploadRes.data?.data?.url || uploadRes.data?.url || '';
      }
      const payload = { ...staffForm, profileImage, phone: staffForm.phone ? (staffForm.phone.startsWith('+') ? staffForm.phone : staffCountryCode + staffForm.phone) : '' };
      if (!profileImage) delete payload.profileImage;
      if (staffEditId) {
        if (!payload.password) delete payload.password;
        await updateStaffMember(staffEditId, payload);
        toast.success('Staff updated');
      } else {
        await createStaffMember(payload);
        toast.success('Staff created');
      }
      setStaffModalOpen(false);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save staff');
    } finally {
      setStaffSubmitting(false);
    }
  };

  const handleRoleSubmit = async () => {
    if (!roleForm.name.trim() || !roleForm.uniqueId.trim()) {
      toast.error('Role name and unique ID are required');
      return;
    }
    setRoleSubmitting(true);
    try {
      const payload = {
        ...roleForm,
        accessiblePages: roleForm.accessiblePages
          ? roleForm.accessiblePages.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };
      await createStaffRole(payload);
      toast.success('Role created');
      setRoleModalOpen(false);
      setRoleForm({ name: '', uniqueId: '', description: '', accessiblePages: '' });
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create role');
    } finally {
      setRoleSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteProcessing(true);
    try {
      await deleteStaffMember(deleteTarget);
      toast.success('Staff deleted');
      setDeleteTarget(null);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleteProcessing(false);
    }
  };

  const handleDeleteRole = async (id) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await deleteStaffRole(id);
      toast.success('Role deleted');
      fetchRoles();
    } catch {
      toast.error('Failed to delete role');
    }
  };

  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return `hsl(${hash % 360}, 50%, 50%)`;
  };

  const columns = [
    {
      id: 'name', label: 'Name', sortable: true,
      render: (row) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar src={row.profileImage || ''} sx={{ width: 36, height: 36, bgcolor: stringToColor(row.name), fontSize: 14, fontWeight: 600 }}>
            {!row.profileImage && (row.name ? row.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '??')}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
            <Typography variant="caption" color="text.secondary">{row.email}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      id: 'uniqueId', label: 'Unique ID',
      render: (row) => (
        <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="primary.main">
          {row.uniqueId}
        </Typography>
      ),
    },
    {
      id: 'roles', label: 'Role(s)',
      render: (row) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(row.roles || []).length > 0 ? row.roles.map((r) => (
            <Chip key={r._id || r} label={r.name || r} size="small" color="primary" variant="outlined" sx={{ fontWeight: 500, fontSize: 11 }} />
          )) : <Typography variant="caption" color="text.disabled">—</Typography>}
        </Stack>
      ),
    },
    {
      id: 'password', label: 'Password',
      render: (row) => (
        <Typography variant="body2" fontFamily="monospace" color="text.secondary">
          ••••••••
        </Typography>
      ),
    },
    {
      id: 'phone', label: 'Contact',
      render: (row) => (
        <Box>
          <Typography variant="body2">{row.phone || '—'}</Typography>
        </Box>
      ),
    },
    {
      id: 'address', label: 'Address',
      render: (row) => (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {[row.addressLine1, row.city, row.state].filter(Boolean).join(', ') || '—'}
        </Typography>
      ),
    },
    {
      id: 'actions', label: 'Actions',
      render: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View details">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setViewStaff(row); }}>
              <VisibilityIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openStaffModal(row); }}>
              <EditIcon fontSize="small" color="warning" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row._id); }}>
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <PageHeader title="Staff Management" subtitle="Manage staff members and their roles">
        <Button variant="outlined" startIcon={<WorkIcon />} onClick={() => setRoleModalOpen(true)}>
          Add Role
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openStaffModal()}>
          Add Staff
        </Button>
      </PageHeader>

      {/* Search */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <SearchBar value={search} onChange={setSearch} placeholder="Search by name, email, or ID..." />
      </Stack>

      {/* Staff Table */}
      <DataTable
        columns={columns}
        rows={staff}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        getRowId={(r) => r._id}
        loading={loading}
        emptyMessage="No staff members found"
      />

      {/* ── Add / Edit Staff Modal ── */}
      <Dialog open={staffModalOpen} onClose={() => setStaffModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {staffEditId ? 'Edit Staff Member' : 'Add Staff Member'}
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Stack direction="row" spacing={2}>
            <TextField label="Full Name" required fullWidth size="small"
              value={staffForm.name} onChange={(e) => setStaffForm(p => ({ ...p, name: e.target.value }))} />
            <TextField label="Email" required fullWidth size="small" type="email"
              value={staffForm.email} onChange={(e) => setStaffForm(p => ({ ...p, email: e.target.value }))} />
          </Stack>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
              Profile Image
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={profileImagePreview || ''}
                sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 20, fontWeight: 600 }}
              >
                {!profileImagePreview && staffForm.name ? staffForm.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : ''}
              </Avatar>
              <Button variant="outlined" component="label" size="small">
                {profileImagePreview ? 'Change' : 'Upload'}
                <input type="file" accept="image/*" hidden onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setProfileImageFile(file);
                    setProfileImagePreview(URL.createObjectURL(file));
                  }
                }} />
              </Button>
              {profileImagePreview && (
                <Button size="small" color="error" onClick={() => {
                  setProfileImageFile(null);
                  setProfileImagePreview(null);
                }}>
                  Remove
                </Button>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={2}>
            <TextField label="Unique ID (auto-generated if empty)" fullWidth size="small"
              placeholder="e.g. JOHN123456"
              value={staffForm.uniqueId} onChange={(e) => setStaffForm(p => ({ ...p, uniqueId: e.target.value.toUpperCase() }))} />
            <TextField label={staffEditId ? 'New Password (leave blank to keep)' : 'Password'} required={!staffEditId} fullWidth size="small" type="password"
              value={staffForm.password} onChange={(e) => setStaffForm(p => ({ ...p, password: e.target.value }))} />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select value={staffCountryCode} onChange={(e) => setStaffCountryCode(e.target.value)}>
                {countryCodes.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Phone" fullWidth size="small"
              value={staffForm.phone} onChange={(e) => setStaffForm(p => ({ ...p, phone: e.target.value }))} />
          </Stack>

          <FormControl size="small" fullWidth>
            <InputLabel>Assign Roles</InputLabel>
            <Select
              multiple
              value={staffForm.roles}
              onChange={(e) => setStaffForm(p => ({ ...p, roles: e.target.value }))}
              input={<OutlinedInput label="Assign Roles" />}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {selected.length === 0 ? (
                    <Chip label="None" size="small" variant="outlined" sx={{ color: 'text.disabled', borderColor: 'divider' }} />
                  ) : selected.map((id) => {
                    const r = roles.find(rr => rr._id === id);
                    return r ? (
                      <Chip
                        key={id}
                        label={r.name}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onDelete={(e) => { e.stopPropagation(); e.preventDefault(); setStaffForm(p => ({ ...p, roles: p.roles.filter(rid => rid !== id) })); }}
                      />
                    ) : null;
                  })}
                </Stack>
              )}
            >
              {roles.map((r) => (
                <MenuItem key={r._id} value={r._id}>
                  {r.name} <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>({r.uniqueId})</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Address</Typography>
          <Stack direction="row" spacing={2}>
            <TextField label="Address Line 1" fullWidth size="small"
              value={staffForm.addressLine1} onChange={(e) => setStaffForm(p => ({ ...p, addressLine1: e.target.value }))} />
            <TextField label="Address Line 2" fullWidth size="small"
              value={staffForm.addressLine2} onChange={(e) => setStaffForm(p => ({ ...p, addressLine2: e.target.value }))} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="City" fullWidth size="small"
              value={staffForm.city} onChange={(e) => setStaffForm(p => ({ ...p, city: e.target.value }))} />
            <TextField label="State" fullWidth size="small"
              value={staffForm.state} onChange={(e) => setStaffForm(p => ({ ...p, state: e.target.value }))} />
            <TextField label="Pincode" fullWidth size="small"
              value={staffForm.pincode} onChange={(e) => setStaffForm(p => ({ ...p, pincode: e.target.value }))} />
            <TextField label="Country" fullWidth size="small"
              value={staffForm.country} onChange={(e) => setStaffForm(p => ({ ...p, country: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setStaffModalOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleStaffSubmit} disabled={staffSubmitting}>
            {staffSubmitting ? <CircularProgress size={20} /> : (staffEditId ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View Staff Detail Modal ── */}
      <Dialog open={!!viewStaff} onClose={() => setViewStaff(null)} maxWidth="sm" fullWidth>
        {viewStaff && (
          <>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={viewStaff.profileImage || ''} sx={{ width: 44, height: 44, bgcolor: stringToColor(viewStaff.name), fontSize: 16, fontWeight: 600 }}>
                {!viewStaff.profileImage && viewStaff.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
              </Avatar>
              <Box>
                <Typography variant="h6">{viewStaff.name}</Typography>
                <Typography variant="caption" color="text.secondary">{viewStaff.uniqueId}</Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Stack direction="row" spacing={4}>
                  <Box><Typography variant="caption" color="text.secondary">Email</Typography><Typography variant="body2">{viewStaff.email}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Phone</Typography><Typography variant="body2">{viewStaff.phone || '—'}</Typography></Box>
                </Stack>
                <Box>
                  <Typography variant="caption" color="text.secondary">Roles</Typography>
                  <Stack direction="row" spacing={0.5} mt={0.5}>
                    {(viewStaff.roles || []).length > 0 ? viewStaff.roles.map((r) => (
                      <Chip key={r._id || r} label={r.name || r} size="small" color="primary" variant="outlined" />
                    )) : <Typography variant="body2" color="text.disabled">No roles assigned</Typography>}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography variant="body2">
                    {[viewStaff.addressLine1, viewStaff.addressLine2, viewStaff.city, viewStaff.state, viewStaff.pincode, viewStaff.country].filter(Boolean).join(', ') || 'No address provided'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={4}>
                  <Box><Typography variant="caption" color="text.secondary">Created</Typography><Typography variant="body2">{new Date(viewStaff.createdAt).toLocaleDateString()}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Last Login</Typography><Typography variant="body2">{viewStaff.lastLogin ? new Date(viewStaff.lastLogin).toLocaleDateString() : 'Never'}</Typography></Box>
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewStaff(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Add Role Modal ── */}
      <Dialog open={roleModalOpen} onClose={() => setRoleModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Staff Role</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Role Name" required fullWidth size="small"
            value={roleForm.name} onChange={(e) => setRoleForm(p => ({ ...p, name: e.target.value }))} />
          <TextField label="Unique ID" required fullWidth size="small"
            placeholder="e.g. ADMIN, SUPR, MGR"
            value={roleForm.uniqueId} onChange={(e) => setRoleForm(p => ({ ...p, uniqueId: e.target.value.toUpperCase() }))} />
          <TextField label="Description" fullWidth size="small" multiline rows={2}
            value={roleForm.description} onChange={(e) => setRoleForm(p => ({ ...p, description: e.target.value }))} />
          <TextField label="Accessible Pages" fullWidth size="small"
            placeholder="/shop, /products, /users"
            helperText="Enter page routes separated by commas"
            value={roleForm.accessiblePages}
            onChange={(e) => setRoleForm(p => ({ ...p, accessiblePages: e.target.value }))} />

          <Button variant="contained" onClick={handleRoleSubmit} disabled={roleSubmitting} sx={{ mt: 1 }}>
            {roleSubmitting ? <CircularProgress size={20} /> : 'Create Role'}
          </Button>

          {/* Existing roles table */}
          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>Existing Roles</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Unique ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Pages</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center"><Typography variant="caption" color="text.secondary">No roles created yet</Typography></TableCell></TableRow>
                ) : roles.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{r.name}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontFamily="monospace" color="primary.main" fontWeight={600}>{r.uniqueId}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{r.description || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(r.accessiblePages || []).length > 0 ? r.accessiblePages.join(', ') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleDeleteRole(r._id)}>
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Staff Member?"
        message="This will permanently remove this staff member. Are you sure?"
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        severity="error"
        loading={deleteProcessing}
      />
    </Box>
  );
}
